/**
 * A Raw DDEX row in the Track Matching table has the id `d_` followed by the Raw DDEX track's id, for example `d_42`.
 * The prefix keeps it apart from the Live Track row's plain id and the Spotify row's `sp_` id.
 * Every place that builds or reads that id goes through this file, so they all follow one rule.
 */

const RAW_DDEX_ROW_ID_PREFIX = 'd_';

/** The Track Matching row id for a Raw DDEX track. */
export function rawDdexRowId(rawDdexTrackId: number | string): string {
    return `${RAW_DDEX_ROW_ID_PREFIX}${rawDdexTrackId}`;
}

/**
 * A record id that is a positive whole number, read from a number or from a string of its digits.
 * Anything else returns null, including zero, negative numbers, fractions and strings with other characters.
 * Live Track row ids and Raw DDEX row ids both use this rule, so the two kinds of row agree on which ids open.
 */
export function parsePositiveWholeId(rawId: unknown): number | null {
    if (typeof rawId === 'number') {
        return Number.isInteger(rawId) && rawId > 0 ? rawId : null;
    }
    if (typeof rawId !== 'string' || !/^\d+$/.test(rawId)) return null;
    const numericId = Number(rawId);
    return numericId > 0 ? numericId : null;
}

/**
 * The Raw DDEX track id inside a Track Matching row id, or null when there is none.
 * It accepts `d_42`, and also a bare `42` or 42, because some rows come straight off the search payload without the prefix.
 * Anything else returns null, including `d_12x`, zero and negative numbers.
 */
export function parseRawDdexRowId(rowId: unknown): number | null {
    if (typeof rowId === 'string' && rowId.startsWith(RAW_DDEX_ROW_ID_PREFIX)) {
        return parsePositiveWholeId(rowId.slice(RAW_DDEX_ROW_ID_PREFIX.length));
    }
    return parsePositiveWholeId(rowId);
}
